import {IonCard, IonCardContent, IonCardHeader, IonIcon, IonList} from "@ionic/react";
import React, {useEffect, useMemo, useRef, useState} from "react";
import createAxiosInstance from "../services/AxiosInstance";
import {useStomp} from "../services/useStomp";
import "./NewDashboard.css"
import LineChartComponent from "./stats/LineChartComponent";
import LineChartTotals from "./stats/LineChartTotals";
import RadarChartDertigen from "./stats/RadarChartDertigen";
import QRCodeComponent from "./shared/QRCodeComponent";
import {Move} from "../model/Move";
import {TotalsMove} from "../model/TotalsMove";
import {User} from "../model/User";
import {v4 as uuidv4} from 'uuid';
import {flashOutline, mapOutline, speedometerOutline, statsChartOutline, timerOutline} from "ionicons/icons";

interface LobbyStats {
    lobbyMoves: Move[];
    amountOfDoublers: number;
    amountOfStreetThrows: number;
    slowestThrower: { username: string; averageThrowTime: number } | null;
    fastestThrower: { username: string; averageThrowTime: number } | null;
    avgAmountOfThrows: number;
    lobbyId: number;
}

interface ThrowerStat {
    username: string;
    averageThrowTime: number;
}

interface DashboardEvent {
    id: string;
    message: string;
    time: string;
}

const Dashboard: React.FC = () => {
    const [guests, setGuests] = useState<User[]>([]);
    const [eventMessages, setEventMessages] = useState<DashboardEvent[]>([]);
    const axios = createAxiosInstance();
    const [lobbyMoves, setLobbyMoves] = useState<Move[]>([]);
    const [totals, setTotals] = useState<TotalsMove[]>([]);
    const [amountOfDoublers, setAmountOfDoublers] = useState<number>(0);
    const [amountOfStreets, setAmountOfStreets] = useState<number>(0);
    const [slowestThrower, setSlowestThrower] = useState<ThrowerStat | null>(null);
    const [fastestThrower, setFastestThrower] = useState<ThrowerStat | null>(null);
    const [avgAmountOfTurns, setAvgAmountOfTurns] = useState<number | null>(null);
    const qrValueRef = useRef<string>('');
    const [lobbyId, setLobbyId] = useState<number>();
    const {subscribe} = useStomp();
    const [sessionId] = useState<string>(() => uuidv4());
    const currentLobbyIdRef = useRef<number | undefined>(undefined);

    useEffect(() => { currentLobbyIdRef.current = lobbyId; }, [lobbyId]);

    function fetchPresentGuests() {
        axios.get('/evening/present-guests')
            .then(res => {
                setGuests(Array.isArray(res.data) ? res.data : []);
            })
    }

    const fetchInitialStats = () => {
        axios.get<LobbyStats>("/lobbies/lobby-statistics")
            .then(res => {
                const data = res.data;
                setLobbyMoves(data.lobbyMoves);
                setAmountOfDoublers(data.amountOfDoublers);
                setAmountOfStreets(data.amountOfStreetThrows);
                setSlowestThrower(data.slowestThrower);
                setFastestThrower(data.fastestThrower);
                setAvgAmountOfTurns(data.avgAmountOfThrows);
                setLobbyId(data.lobbyId);
            })
            .catch(console.error);
    };

    const fetchTotalsData = async () => {
        try {
            const response = await axios.get('/lobbies/totals');
            setTotals(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchStatisticsByLobbyId = async (lobbyId: number | undefined) => {
        if (lobbyId === undefined || lobbyId === null) return;
        axios.get<LobbyStats>(`/lobbies/lobby-statistics?lobbyId=${lobbyId}`)
            .then(res => {
                const data = res.data;
                setLobbyMoves(data.lobbyMoves);
                setAmountOfDoublers(data.amountOfDoublers);
                setAmountOfStreets(data.amountOfStreetThrows);
                setSlowestThrower(data.slowestThrower);
                setFastestThrower(data.fastestThrower);
                setAvgAmountOfTurns(data.avgAmountOfThrows);
                setLobbyId(data.lobbyId);
            })
            .catch(console.error);
    }


    useEffect(() => {
        fetchPresentGuests()
        fetchInitialStats()
        fetchTotalsData()
    }, [])

    useEffect(() => {
        fetchStatisticsByLobbyId(lobbyId);
    }, [lobbyId]);

    useEffect(() => {
        if (!lobbyId) return;

        const unsubStats = subscribe(
            `/topic/dertigen/lobby/${lobbyId}/statistics`,
            (stats: LobbyStats) => {
                if (stats.lobbyId !== currentLobbyIdRef.current) return;

                setLobbyMoves(stats.lobbyMoves);
                setAmountOfDoublers(stats.amountOfDoublers);
                setAmountOfStreets(stats.amountOfStreetThrows);
                setSlowestThrower(stats.slowestThrower);
                setFastestThrower(stats.fastestThrower);
                setAvgAmountOfTurns(stats.avgAmountOfThrows);
            }
        );

        const unsubEvents = subscribe(
            `/topic/dashboard`,
            (raw: string) => {
                const now = new Date();
                const pad2 = (n: number) => n.toString().padStart(2, "0");
                const timestamp = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;

                let messageText: string;

                try {
                    const parsed = JSON.parse(raw) as { message: string };
                    messageText = parsed.message;
                } catch {
                    messageText = raw;
                }

                setEventMessages(prev => [
                    {id: uuidv4(), message: messageText, time: timestamp},
                    ...prev
                ].slice(0, 40));
            }
        );

        const unsubPresentUsers = subscribe(
            `/topic/dashboard/present-users`,
            (raw: User[]) => {
                try {
                    const parsed = raw;
                    setGuests(parsed);
                } catch (err) {
                    console.error("Failed to parse present-users payload:", err);
                }
            }
        );

        return () => {
            unsubStats();
            unsubEvents();
            unsubPresentUsers();
        };
    }, [lobbyId]);

    useEffect(() => {
        return subscribe(
            `/topic/dashboard/${sessionId}`,
            (raw: string) => {
                const {lobbyId: scannedLobby} = JSON.parse(raw);
                setLobbyId(scannedLobby);
            }
        );
    }, [sessionId]);

    useEffect(() => {
        if (!sessionId) return;
        qrValueRef.current = sessionId;
    }, [sessionId]);

    const topEvents = useMemo(() => eventMessages.slice(0, 40), [eventMessages]);

    const renderThrower = (thrower: ThrowerStat | null) => {
        if (!thrower) {
            return {name: '—', time: 'Geen data'};
        }
        return {
            name: thrower.username,
            time: `${(thrower.averageThrowTime / 1000).toFixed(1)} sec / worp`
        };
    };

    const fastest = renderThrower(fastestThrower);
    const slowest = renderThrower(slowestThrower);

    const statsRows = [
        {
            title: 'Verdubbelaars',
            value: amountOfDoublers,
            icon: flashOutline
        },
        {
            title: 'Straatjes',
            value: amountOfStreets,
            icon: mapOutline
        },
        {
            title: 'Gem. beurten',
            value: avgAmountOfTurns !== null ? avgAmountOfTurns.toFixed(1) : '—',
            icon: statsChartOutline
        },
        {
            title: 'Snelste gooier',
            value: fastest.name,
            meta: fastest.time,
            icon: speedometerOutline
        },
        {
            title: 'Langzaamste gooier',
            value: slowest.name,
            meta: slowest.time,
            icon: timerOutline
        }
    ];

    return (
        <div className="dashboard-shell">
            <section className="dashboard-guests" aria-label="Aanwezige gasten">
                {guests.length === 0 ? (
                    <p className="dashboard-empty-note">Er is nog niemand ingecheckt.</p>
                ) : (
                    guests.map((user) => (
                        <div key={user.id} className="guest-chip">
                            <div
                                className={`guest-avatar-ring ${
                                    user?.balance !== undefined && user?.balance < 0 ? "is-negative" : "is-positive"
                                }`}
                            >
                                <img
                                    src={
                                        user.profilePictureUrl !== undefined
                                            ? `${process.env.REACT_APP_STORAGE}${user.profilePictureUrl}`
                                            : `${process.env.REACT_APP_STORAGE}default-profile.jpg`
                                    }
                                  alt={user.name}
                                    className="guest-avatar"
                                />
                            </div>
                            <p className="guest-name">{user.name}</p>
                        </div>
                    ))
                )}
            </section>

            <section className="dashboard-main-grid">
                <div className="dashboard-left-stack">
                    <div className="dashboard-kpi-grid">
                        <IonCard className="dashboard-card qr-card">
                            <IonCardHeader className="dashboard-card-header">
                                <h3>Koppel dashboard</h3>
                                <p>Scan met controller om van lobby te wisselen</p>
                            </IonCardHeader>
                            <IonCardContent className="dashboard-card-content qr-content">
                                {qrValueRef.current && <QRCodeComponent value={qrValueRef.current}/>} 
                                <small className="session-id">Sessie: {sessionId.slice(0, 8)}</small>
                            </IonCardContent>
                        </IonCard>

                        <IonCard className="dashboard-card stats-summary-card">
                            <IonCardHeader className="dashboard-card-header">
                                <h3>Lobby samenvatting</h3>
                                <p>Realtime kernstatistieken met consistente iconen</p>
                            </IonCardHeader>
                            <IonCardContent className="dashboard-card-content stats-summary-content">
                                {statsRows.map((row) => (
                                    <div className="stats-row" key={row.title}>
                                        <div className="stats-row-left">
                                            <span className="stats-row-icon" aria-hidden="true">
                                                <IonIcon icon={row.icon}/>
                                            </span>
                                            <span className="stats-row-title">{row.title}</span>
                                        </div>
                                        <div className="stats-row-right">
                                            <span className="stats-row-value">{row.value}</span>
                                            {row.meta && <small className="stats-row-meta">{row.meta}</small>}
                                        </div>
                                    </div>
                                ))}
                            </IonCardContent>
                        </IonCard>
                    </div>

                    <IonCard className="dashboard-card chart-card radar-card main-radar-card">
                        <IonCardHeader className="dashboard-card-header">
                            <h3>Dertigen spelersprofiel</h3>
                            <p>Gem. drinken per drinkbeurt, drinkbeurten en verdubbelpercentage</p>
                        </IonCardHeader>
                        <IonCardContent className="dashboard-card-content chart-content radar-content">
                            <RadarChartDertigen lobbyMoves={lobbyMoves}/>
                        </IonCardContent>
                    </IonCard>
                </div>

                <IonCard className="dashboard-card feed-card">
                    <IonCardHeader className="dashboard-card-header">
                        <h3>Activiteit</h3>
                        <p>Laatste {topEvents.length} events</p>
                    </IonCardHeader>
                    <IonCardContent className="dashboard-card-content feed-content">
                        <IonList class="feed-list">
                            {topEvents.length === 0 && (
                                <p className="dashboard-empty-note">(Nog) geen events gevonden.</p>
                            )}
                            {topEvents.map((chatMessage) => (
                                <div className="feed-item" key={chatMessage.id}>
                                    <span className="feed-message">{chatMessage.message}</span>
                                    <span className="feed-time">{chatMessage.time}</span>
                                </div>
                            ))}
                        </IonList>
                    </IonCardContent>
                </IonCard>
            </section>

            <section className="dashboard-chart-grid">
                <IonCard className="dashboard-card chart-card">
                    <IonCardHeader className="dashboard-card-header">
                        <h3>Lobby trend</h3>
                        <p>Cumulatieve strepen binnen huidige lobby</p>
                    </IonCardHeader>
                    <IonCardContent className="dashboard-card-content chart-content">
                        <LineChartComponent lobbyMoves={lobbyMoves}/>
                    </IonCardContent>
                </IonCard>

                <IonCard className="dashboard-card chart-card">
                    <IonCardHeader className="dashboard-card-header">
                        <h3>Historische trend</h3>
                        <p>Cumulatieve strepen over alle sessies</p>
                    </IonCardHeader>
                    <IonCardContent className="dashboard-card-content chart-content">
                        <LineChartTotals data={totals}/>
                    </IonCardContent>
                </IonCard>
            </section>
        </div>
    );
}
export default Dashboard;
